import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, Layers, RefreshCw, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';

function MetricCard({ label, value, subtitle, color = 'text-white' }) {
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
}

function DataTable({ columns, rows, emptyMessage = 'No data available' }) {
  if (!rows?.length) {
    return <p className="text-slate-500 text-sm py-4 text-center">{emptyMessage}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            {columns.map(col => (
              <th key={col.key} className="text-left py-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
              {columns.map(col => (
                <td key={col.key} className="py-2 px-3 text-slate-300">
                  {col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function pct(val) {
  if (val == null) return '—';
  return `${(val * 100).toFixed(1)}%`;
}

export default function ExtractionAccuracyTab() {
  const [accuracy, setAccuracy] = useState([]);
  const [calibration, setCalibration] = useState([]);
  const [matchSource, setMatchSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [accRes, calRes, msRes] = await Promise.all([
        apiService.fetchDashboardView('ExtractionAccuracy'),
        apiService.fetchDashboardView('ConfidenceCalibration'),
        apiService.fetchDashboardView('MatchSourceDistribution'),
      ]);
      setAccuracy(accRes.data || []);
      setCalibration(calRes.data || []);
      setMatchSource(msRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-medium">Failed to load dashboard data</p>
          <p className="text-red-400/70 text-sm">{error}</p>
        </div>
        <button onClick={loadData} className="ml-auto px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  // Latest month summary from accuracy data
  const latest = accuracy.length > 0 ? accuracy[accuracy.length - 1] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">Extraction Accuracy</h2>
        </div>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      {latest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Item Accuracy"
            value={pct(latest.ItemAccuracy)}
            subtitle={`${latest.ItemCorrect} / ${latest.TotalItems - latest.DeletedItems} items correct`}
            color={latest.ItemAccuracy >= 0.85 ? 'text-emerald-400' : latest.ItemAccuracy >= 0.7 ? 'text-amber-400' : 'text-red-400'}
          />
          <MetricCard
            label="Quantity Accuracy"
            value={pct(latest.QtyAccuracy)}
            subtitle={`${latest.QtyCorrect} / ${latest.TotalItems - latest.DeletedItems} quantities correct`}
            color={latest.QtyAccuracy >= 0.9 ? 'text-emerald-400' : latest.QtyAccuracy >= 0.75 ? 'text-amber-400' : 'text-red-400'}
          />
          <MetricCard
            label="Total Items Processed"
            value={latest.TotalItems?.toLocaleString() || '0'}
            subtitle={`${latest.DeletedItems || 0} deleted by CSR`}
          />
          <MetricCard
            label="LLM Model"
            value={latest.LlmModel || '—'}
            subtitle={`Month: ${latest.Month}`}
          />
        </div>
      )}

      {/* Accuracy by Month */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-slate-400" />
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Accuracy by Month</h3>
        </div>
        <DataTable
          columns={[
            { key: 'Month', label: 'Month' },
            { key: 'LlmModel', label: 'Model' },
            { key: 'TotalItems', label: 'Total Items' },
            { key: 'ItemAccuracy', label: 'Item Accuracy', render: pct },
            { key: 'QtyAccuracy', label: 'Qty Accuracy', render: pct },
            { key: 'UomCorrect', label: 'UOM Correct' },
            { key: 'DeletedItems', label: 'Deleted' },
          ]}
          rows={accuracy}
        />
      </div>

      {/* Confidence Calibration */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-slate-400" />
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Confidence Calibration</h3>
          <span className="text-xs text-slate-600 ml-2">When we say "high confidence", how often does the CSR agree?</span>
        </div>
        <DataTable
          columns={[
            { key: 'ConfidenceBand', label: 'Confidence Band', render: (v) => (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                v === 'high' ? 'bg-emerald-500/10 text-emerald-400' :
                v === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                'bg-red-500/10 text-red-400'
              }`}>{v}</span>
            )},
            { key: 'TotalItems', label: 'Total Items' },
            { key: 'Unchanged', label: 'Unchanged' },
            { key: 'ActualAccuracy', label: 'Actual Accuracy', render: pct },
            { key: 'AvgConfidenceScore', label: 'Avg Score', render: (v) => v != null ? v.toFixed(3) : '—' },
          ]}
          rows={calibration}
        />
      </div>

      {/* Match Source Distribution */}
      <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-slate-400" />
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Match Source Distribution</h3>
          <span className="text-xs text-slate-600 ml-2">How items are being matched in A+</span>
        </div>
        <DataTable
          columns={[
            { key: 'Month', label: 'Month' },
            { key: 'MatchSource', label: 'Source', render: (v) => {
              const labels = { itmst: 'SQL Direct', iaxrf: 'Cross-Ref', azure_search: 'AI Search' };
              return labels[v] || v || 'Manual';
            }},
            { key: 'ItemCount', label: 'Items' },
            { key: 'AvgConfidence', label: 'Avg Confidence', render: (v) => v != null ? v.toFixed(3) : '—' },
            { key: 'AccuracyRate', label: 'CSR Accept Rate', render: pct },
          ]}
          rows={matchSource}
        />
      </div>
    </div>
  );
}
